import { Decision, Ratification, type PacketWithCandidate } from '@promo/shared';
import { getDistinctPacketStatusLabel } from './packetStatus';

export interface ExportablePacketData {
  candidateName: string;
  candidateInitials: string;
  targetTitle: string;
  managerName: string;
  managerEmail: string;
  assessorNames: string;
  status: string;
  finalDecision: string;
  recommendation: string;
  summary: string;
  ratification: string;
  justification: string;
  createdAt: string;
}

function getOverruledDecision(recommendation: Decision | string | null | undefined): Decision | '' {
  if (recommendation === Decision.PROMOTE) return Decision.DEFER;
  if (recommendation === Decision.DEFER) return Decision.PROMOTE;
  return '';
}

function getFinalDecision(packet: PacketWithCandidate, recommendation: string): string {
  if (packet.ratification === Ratification.RATIFIED) {
    const finalDecision = packet.decision ?? recommendation;
    if (finalDecision) return finalDecision;
  }

  if (packet.ratification === Ratification.OVERRULED) {
    const finalDecision = packet.decision ?? getOverruledDecision(recommendation);
    if (finalDecision) return finalDecision;
  }

  if (packet.decision) {
    return packet.decision;
  }

  return recommendation ? 'Pending ratification' : 'Pending recommendation';
}

/**
 * Converts packet data to exportable format
 */
export function preparePacketDataForExport(
  packets: PacketWithCandidate[],
  managers: Array<{ id: string; name?: string; email: string }>
): ExportablePacketData[] {
  return packets.map(packet => {
    const manager = managers.find(m => m.id === packet.managerId);
    
    // Determine justification based on ratification
    let justification = '';
    if (packet.ratification === Ratification.OVERRULED) {
      justification = packet.decisionJustification || packet.overruleJustification || '';
    } else if (packet.ratification === Ratification.RATIFIED) {
      justification = 'No justification needed';
    }

    const recommendation = packet.recommendation ?? '';
    const finalDecision = getFinalDecision(packet, recommendation);
    const assessorNames = (packet.assessors ?? [])
      .map((assessor) => assessor.name?.trim() || assessor.email)
      .filter(Boolean)
      .join('; ');

    return {
      candidateName: packet.candidateName || '',
      candidateInitials: packet.candidateInitials || '',
      targetTitle: packet.targetTitle || '',
      managerName: manager?.name || manager?.email || '',
      managerEmail: manager?.email || '',
      assessorNames,
      status: getDistinctPacketStatusLabel(packet.status),
      finalDecision,
      recommendation,
      summary: packet.summary || '',
      ratification: packet.ratification || '',
      justification,
      createdAt: packet.createdAt ? new Date(packet.createdAt).toLocaleDateString() : '',
    };
  });
}

/**
 * Converts data to CSV format
 */
export function convertToCSV(data: ExportablePacketData[]): string {
  if (data.length === 0) return '';

  const headers = [
    'Candidate Name',
    'Candidate Initials', 
    'Target Title',
    'Manager Name',
    'Manager Email',
    'Assessor Names',
    'Status',
    'Final Decision',
    'Recommendation',
    'Summary',
    'Ratification',
    'Justification',
    'Created Date'
  ];

  const csvRows = [
    headers.join(','),
    ...data.map(row => [
      escapeCsvValue(row.candidateName),
      escapeCsvValue(row.candidateInitials),
      escapeCsvValue(row.targetTitle),
      escapeCsvValue(row.managerName),
      escapeCsvValue(row.managerEmail),
      escapeCsvValue(row.assessorNames),
      escapeCsvValue(row.status),
      escapeCsvValue(row.finalDecision),
      escapeCsvValue(row.recommendation),
      escapeCsvValue(row.summary),
      escapeCsvValue(row.ratification),
      escapeCsvValue(row.justification),
      escapeCsvValue(row.createdAt),
    ].join(','))
  ];

  return csvRows.join('\n');
}

/**
 * Escapes CSV values to handle commas, quotes, and newlines
 */
function escapeCsvValue(value: string): string {
  if (!value) return '';
  
  // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  
  return value;
}

/**
 * Downloads CSV data as a file
 */
export function downloadCSV(csvData: string, filename: string = 'packets-export.csv'): void {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
